import React, { useState, FormEvent, useCallback, useContext } from "react";
import { Modal, Form, Button } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useErrors } from "services/customHooks/useErrors";
import { NpaNxxModel, DefaultNpaNxxModel } from "../types/NpaNxxModel";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { HasErrors, Errors, AddError } from "components/framework/errorHandling/ErrorUtil";
import { NpaNxxApi } from "services/apis/NpaNxxApi";
import { Moment } from "moment";
import { showInfoNotification } from "components/framework/notification/NotificationUtil";
import { handleError, getFieldErrors } from "services/util/ApiUtil";
import CloseButton from "components/framework/modals/CloseButton";
import TextFormInput from "components/framework/forms/TextFormInput";
import { nameOf } from "services/util/ObjectUtil";
import DateTimeFormInput from "components/framework/forms/DateTimeFormInput";
import DropdownFormInput from "components/framework/forms/DropdownFormInput";
import { npacRegionDropdownOptions, NpacRegion } from "services/apis/types/number/NpacRegion";
import { isNumber } from "services/util/StringUtil";
import { AppContext } from "services/appContext/AppContext";

type Props = {
  closeModal: () => void;
};

export default function CreateNpaNxxModal(props: Props) {
  const { setErrors, getErrorHandler } = useErrors();
  const [npaNxxModel, setNpaNxxModel] = useState<NpaNxxModel>(DefaultNpaNxxModel);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const intl = useIntl();
  const isMounted = useIsMounted();
  const { appContext } = useContext(AppContext);
  const theme = appContext.theme;

  const closeModal = useCallback(props.closeModal, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setShowLoadingIndicator(true);

    const npaNxxObject: NpaNxxModel = {
      npaNxx: npaNxxModel.npaNxx.trim(),
      npacRegion: npaNxxModel.npacRegion,
      effectiveDate: npaNxxModel.effectiveDate
    };
    const errors = validateNpaNxx(npaNxxObject);

    if (HasErrors(errors)) {
      setErrors(errors);
      if (isMounted.current) {
        setShowLoadingIndicator(false);
      }
    } else {
      NpaNxxApi.create(npaNxxModel.npaNxx, {
        effectiveDate: npaNxxModel.effectiveDate.toDate(),
        npacRegion: npaNxxModel.npacRegion
      })
        .then(() => {
          showInfoNotification(intl.formatMessage({ id: "network.npaNxx.edit.successNotificationMessage" }));
          closeModal();
        })
        .catch(error => {
          handleError(error);
          if (isMounted.current) {
            const errorsResult = getFieldErrors(error.fieldErrors);
            setErrors(errorsResult);
          }
        })
        .finally(() => {
          if (isMounted.current) {
            setShowLoadingIndicator(false);
          }
        });
    }
  };

  return (
    <Modal className="modal-dialog-centered modal-sm" isOpen={true}>
      <div className="modal-header pb-0">
        <h5 className="modal-title">
          <FormattedMessage id="network.npaNxx.edit.modalTitle" />
        </h5>
        <CloseButton close={props.closeModal} />
      </div>
      <Form onSubmit={e => handleSubmit(e)}>
        <div className="modal-body pb-0">
          <TextFormInput
            labelTranslationId="network.npaNxx.edit.label"
            value={npaNxxModel.npaNxx}
            handleInputChange={(value: string) => setNpaNxxModel({ ...npaNxxModel, npaNxx: value })}
            errorHandler={getErrorHandler(nameOf<NpaNxxModel>("npaNxx"))}
          />
          <DateTimeFormInput
            labelTranslationId="network.npaNxx.edit.effectiveDateLabel"
            value={npaNxxModel.effectiveDate}
            handleInputChange={(value: Moment) => setNpaNxxModel({ ...npaNxxModel, effectiveDate: value })}
            errorHandler={getErrorHandler(nameOf<NpaNxxModel>("effectiveDate"))}
          />
          <DropdownFormInput
            labelTranslationId="network.lrn.create.npacRegionLabel"
            required
            value={npaNxxModel.npacRegion}
            handleInputChange={(value: string) => setNpaNxxModel({ ...npaNxxModel, npacRegion: value })}
            errorHandler={getErrorHandler(nameOf<NpaNxxModel>("npacRegion"))}
            options={npacRegionDropdownOptions}
          />
        </div>
        <div className="modal-footer">
          <Button color="link" type="button" className={`${theme === "light" ? "lblue-color" : ""}`} onClick={() => props.closeModal()}>
            <FormattedMessage id="network.npaNxx.edit.cancelButton" />
          </Button>
          <Button color="primary" type="submit" className={`ml-auto ${theme === "light" ? "bg-lblue no-border" : ""}`}>
            {showLoadingIndicator && <i className="fas fa-spinner fa-spin mr-2" />}
            <FormattedMessage id="network.npaNxx.edit.submitButton" />
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

const validateNpaNxx = (npaNxx: NpaNxxModel) => {
  const errors: Errors = {};

  if (!npaNxx.npaNxx || npaNxx.npaNxx.length !== 6 || !isNumber(npaNxx.npaNxx)) {
    AddError(errors, nameOf<NpaNxxModel>("npaNxx"), "network.npaNxx.edit.invalidNpaNxx");
  }

  if (!npaNxx.npacRegion || !NpacRegion[npaNxx.npacRegion]) {
    AddError(errors, nameOf<NpaNxxModel>("npacRegion"), "network.npaNxx.edit.invalidNpacRegion");
  }

  if (!npaNxx.effectiveDate) {
    AddError(errors, nameOf<NpaNxxModel>("effectiveDate"), "network.npaNxx.edit.invalidEffectiveDate");
  }

  return errors;
};
